import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  geocoderConfigType,
  geolocationType,
  locationCategoryType,
  locationType as locationPropType,
  arrivalType,
  stopType,
} from '#/prop-types';
// TODO: Address this on the OTP-UI side once we are up to date on the location-field component. Modify to accept theme props for active li
import { LIGHT_THEME } from '#/user/themes';

import LocationTypeIcon from '#/shared/components/LocationTypeIcon';
import GTM_HANDLES from '#/shared/gtm-handles';
import * as Styled from './styled';

const useUpdateLinkStyles = ref => {
  useEffect(() => {
    const updateStyles = () => {
      const links = ref.current.querySelectorAll('a[id*="otpui-locf-option"]');

      links.forEach(link => {
        const style = window.getComputedStyle(link);
        if (style.backgroundColor === `${LIGHT_THEME.activeOmniboxULLIColor}`) {
          link.setAttribute(
            'style',
            `background-color: ${LIGHT_THEME.newOmniboxULLIColor}; color: ${LIGHT_THEME.activeOmniboxSpanColor}; opacity: 1;`
          );

          const spans = link.querySelectorAll('span');
          spans.forEach(spanEl => {
            spanEl.setAttribute(
              'style',
              `color: ${LIGHT_THEME.activeOmniboxSpanColor};`
            );
          });

          const svg = link.querySelector('svg[class*="GeocodedFeatureIcon"]');
          if (svg) {
            svg.setAttribute(
              'style',
              `fill: ${LIGHT_THEME.activeOmniboxSpanColor};`
            );
          }
        }
      });
    };

    const mutationObserver = new MutationObserver(updateStyles);

    if (ref.current) {
      mutationObserver.observe(ref.current, {
        childList: true,
        attributes: true,
        subtree: true,
      });
      updateStyles();
    }

    return () => mutationObserver.disconnect();
  }, [ref]);
};

function Omnibox({
  geocoderConfig,
  location,
  onLocationSelected,
  onNearbyStopSelected,
  addLocationSearch,
  clearLocation,
  currentPosition,
  getCurrentPosition,
  recentSearches,
  nearbyStops,
  favoriteStops,
  locationType,
  suppressNearby,
  onFavoriteStopSelected,
}) {
  const omniboxRef = useRef(null);
  useUpdateLinkStyles(omniboxRef);

  const intl = useIntl();
  const sortedUniqueFavoriteStops = Array.from(
    new Set(favoriteStops.map(stop => stop.stopId))
  )
    .map(id => favoriteStops.find(stop => stop.stopId === id))
    .sort((a, b) => a.stopId - b.stopId);

  return (
    <Styled.OmniboxBody ref={omniboxRef}>
      <Styled.Panel>
        {currentPosition && currentPosition.outOfBounds && (
          <Styled.OutOfBoundsWarning>
            <Styled.OutOfBoundsWarningIconColumn>
              <Styled.OutOfBoundsIcon />
            </Styled.OutOfBoundsWarningIconColumn>
            <Styled.OutOfBoundsWarningTextColumn>
              <FormattedMessage id="location.omniboxoutofbounds" />
            </Styled.OutOfBoundsWarningTextColumn>
          </Styled.OutOfBoundsWarning>
        )}
        <Styled.LocationField
          currentPosition={currentPosition}
          geocoderConfig={geocoderConfig}
          inputPlaceholder={intl.formatMessage({
            id: 'omnibox.searchplaceholder',
          })}
          locationType={locationType}
          onLocationSelected={(otpUiIntl, selectionData) =>
            onLocationSelected(selectionData)
          }
          getCurrentPosition={getCurrentPosition}
          location={location}
          clearLocation={clearLocation}
          addLocationSearch={addLocationSearch}
          suppressNearby={suppressNearby}
          GeocodedOptionIconComponent={Styled.GeocodedFeatureIcon}
          currentPositionIcon={<Styled.CurrentLocationBadge />}
          LocationIconComponent={LocationTypeIcon}
          clearButtonIcon={<Styled.CloseIcon />}
          autoFocus
          isStatic
        />
      </Styled.Panel>
      <Styled.Panel>
        <Styled.PanelSubheading>
          <FormattedMessage id="omnibox.favorites" />
        </Styled.PanelSubheading>
        {favoriteStops &&
          sortedUniqueFavoriteStops.map(fs => (
            <Styled.ButtonListItem
              onClick={() => onFavoriteStopSelected(fs)}
              key={fs.id}
              dataGtm={GTM_HANDLES.omnibox.searchFavoriteButton}
            >
              <Styled.ButtonListIcon
                type="stop"
                title={intl.formatMessage({ id: 'omnibox.favoritestop' })}
              />
              <Styled.ButtonListName>
                {fs.stopName}
                <Styled.ButtonListHint>
                  <FormattedMessage
                    id="omnibox.stopid"
                    values={{ id: fs.stopId }}
                  />
                </Styled.ButtonListHint>
              </Styled.ButtonListName>
            </Styled.ButtonListItem>
          ))}
        {(!favoriteStops || favoriteStops.length === 0) && (
          <Styled.PanelInfo>
            <FormattedMessage id="omnibox.nofavorites" />
          </Styled.PanelInfo>
        )}
      </Styled.Panel>
      <Styled.Panel>
        <Styled.PanelSubheading>
          <FormattedMessage id="omnibox.recent" />
        </Styled.PanelSubheading>
        {recentSearches.map(rs => (
          <Styled.ButtonListItem
            onClick={() => onLocationSelected({ locationType, location: rs })}
            key={rs.name}
            dataGtm={GTM_HANDLES.omnibox.searchRecentButton}
          >
            <Styled.ButtonListIcon type="default" title="Location Type" />
            <Styled.ButtonListName>{rs.name}</Styled.ButtonListName>
          </Styled.ButtonListItem>
        ))}
        {recentSearches.length === 0 && (
          <Styled.PanelInfo>
            <FormattedMessage id="omnibox.norecent" />
          </Styled.PanelInfo>
        )}
      </Styled.Panel>
      {currentPosition.coords && !suppressNearby && (
        <Styled.Panel>
          <Styled.PanelSubheading>
            <FormattedMessage id="omnibox.nearby" />
          </Styled.PanelSubheading>
          {nearbyStops &&
            nearbyStops.map(ns => (
              <Styled.ButtonListItem
                onClick={() => onNearbyStopSelected(ns.locid)}
                key={ns.locid}
                dataGtm={GTM_HANDLES.omnibox.searchNearbyButton}
              >
                <Styled.ButtonListIcon type="stop" title="Nearby Stop" />
                <Styled.ButtonListName>
                  {ns.desc}
                  <Styled.ButtonListHint>
                    <FormattedMessage
                      id="omnibox.stopid"
                      values={{ id: ns.locid }}
                    />
                  </Styled.ButtonListHint>
                </Styled.ButtonListName>
              </Styled.ButtonListItem>
            ))}
          {(!nearbyStops || nearbyStops.length === 0) && (
            <Styled.PanelInfo>
              <FormattedMessage id="omnibox.nonearby" />
            </Styled.PanelInfo>
          )}
        </Styled.Panel>
      )}
    </Styled.OmniboxBody>
  );
}

Omnibox.propTypes = {
  /**
   * Dispatched upon selecting a geocoded result
   * Provides an argument in the format:
   *
   * ```js
   * { location: geocodedLocation }
   * ```
   */
  addLocationSearch: PropTypes.func.isRequired,
  /**
   * Dispatched whenever the clear location button is clicked.
   * Provides an argument in the format:
   *
   * ```js
   * { locationType: string }
   * ```
   */
  clearLocation: PropTypes.func,
  /** The current position of the user if it is available. */
  currentPosition: geolocationType,
  /** A configuration object describing what geocoder should be used. */
  geocoderConfig: geocoderConfigType.isRequired,
  /**
   * This is dispatched when the current position is null. This indicates that
   * the user has requested to use the current position, but that the current
   * position is not currently available. This method sends back the
   * locationType value supplied to the component.
   */
  getCurrentPosition: PropTypes.func,
  /** The location that this component is currently set with. */
  location: locationPropType,
  /** Either `from` or `to` */
  locationType: locationCategoryType.isRequired,
  /**
   * Handler for location selection, the function is passed a single object
   * parameter in the following format:
   *
   * ```js
   * {
   *  locationType: string,
   *  location: object
   * }
   * ```
   */
  onLocationSelected: PropTypes.func.isRequired,
  /** A function to handle when a nearby stop is selected. */
  onNearbyStopSelected: PropTypes.func,
  /** A function to handle when a favorite stop is selected. */
  onFavoriteStopSelected: PropTypes.func.isRequired,
  /** A list of recent searches to show to the user */
  recentSearches: PropTypes.arrayOf(locationPropType),
  /** A list of stops near the user */
  nearbyStops: PropTypes.arrayOf(stopType),
  /** A list of favorite stops */
  favoriteStops: PropTypes.arrayOf(arrivalType),
  /** If true, do not show nearbyStops or current location as options */
  suppressNearby: PropTypes.bool,
};

Omnibox.defaultProps = {
  currentPosition: {
    coords: null,
    error: null,
    fetching: false,
    outOfBounds: false,
  },
  location: null,
  recentSearches: [],
  onNearbyStopSelected: () => {},
  nearbyStops: [],
  favoriteStops: [],
  suppressNearby: false,
  clearLocation: () => {},
  getCurrentPosition: () => {},
};

export default Omnibox;
